import React from 'react';
import { motion } from 'framer-motion';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { TareaConRelaciones, TIPO_TAREA_LABELS, TAMANIO_LABELS, CLASE_SERVICIO_ICONS, CLASE_SERVICIO_LABELS, PRIORIDAD_COLORS, Prioridad } from '../types';

interface TarjetaTareaProps {
    tarea: TareaConRelaciones;
    onClick: (tarea: TareaConRelaciones) => void;
    index?: number;
    // Vista de solo lectura (resumen por fase en PhaseCollapsible) pasa
    // `draggable={false}` — el tablero principal la deja en `true` (default).
    draggable?: boolean;
}

const tipoIcons: Record<string, string> = {
    bug_soporte: '🐛',
    nuevo_modulo: '🚀',
    actualizacion_refactor: '🔄'
};

const getInitials = (nombre: string): string => {
    const partes = nombre.trim().split(/\s+/);
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[1][0]).toUpperCase();
};

export const TarjetaTarea: React.FC<TarjetaTareaProps> = ({ tarea, onClick, index = 0, draggable = true }) => {
    // Drag & drop táctil real (funciona con mouse y con touch) vía dnd-kit —
    // reemplaza la API HTML5 DnD nativa, que no dispara en celulares.
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: tarea.id,
        disabled: !draggable,
    });

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
    };

    const isOverdue = (dateStr: string | null): boolean => {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    };

const calcularDiasEnColumna = (actualizadoEn: string): number => {
    const diffMs = Date.now() - new Date(actualizadoEn).getTime();
    return Math.floor(diffMs / 86400000);
};

const isCompleted = tarea.estado === 'produccion';
const overdue = isOverdue(tarea.fecha_limite) && !isCompleted;
const esExpedito = tarea.clase_servicio === 'expedito';
const diasEnColumna = calcularDiasEnColumna(tarea.actualizado_en);
const edadNivel = diasEnColumna > 14 ? 'critico'
    : diasEnColumna > 7 ? 'alto'
    : diasEnColumna > 3 ? 'medio'
    : 'ok';
const edadColor = {
    critico: 'text-white bg-red-600',
    alto:    'text-white bg-red-500',
    medio:   'text-amber-800 bg-amber-100',
    ok:      'text-slate-500 bg-slate-100'
}[edadNivel];
const edadRing = edadNivel === 'critico' || edadNivel === 'alto'
    ? 'ring-1 ring-red-300'
    : '';

    const dragStyle: React.CSSProperties = {
        transform: transform ? CSS.Translate.toString(transform) : undefined,
        zIndex: isDragging ? 50 : undefined,
        // Con el sensor táctil activo, evita que el navegador interprete el
        // gesto como scroll de la página en vez de arrastre de la tarjeta.
        touchAction: draggable ? 'none' : undefined,
    };

    return (
        <div ref={setNodeRef} style={dragStyle} {...(draggable ? { ...attributes, ...listeners } : {})}>
            <motion.div
                layout
                onClick={() => onClick(tarea)}
                initial={{ opacity: 0, y: 12, scale: 0.95 }}
                animate={{ opacity: isDragging ? 0.5 : 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{
                    layout: { type: 'spring', stiffness: 380, damping: 32 },
                    opacity: { duration: 0.2 },
                    y: { duration: 0.2 },
                    scale: { duration: 0.2 },
                    delay: Math.min(index * 0.03, 0.4)
                }}
                whileHover={{
                    y: -3,
                    scale: 1.015,
                    boxShadow: '0 10px 20px -5px rgba(0,0,0,0.12), 0 4px 6px -2px rgba(0,0,0,0.05)',
                    transition: { duration: 0.15 }
                }}
                whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
                className={`
                    group relative rounded-xl p-3.5 mb-2 border cursor-pointer
                    ${isCompleted
                        ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                        : overdue
                            ? 'bg-red-50/60 border-red-200'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                    }
                    ${esExpedito && !isCompleted ? 'ring-2 ring-red-400 animate-pulse' : ''}
                    ${tarea.prioridad === 'urgente' && !isCompleted ? 'border-red-300 bg-red-50/30' : ''}
                `}
                style={{
                    borderLeftWidth: '3px',
                    borderLeftColor: tarea.cliente_color
                }}
            >
                {/* Fila de cabecera reagrupada por significado, no por orden histórico:
                    izquierda = "qué es esta tarea" (cliente, tipo, origen); derecha =
                    "estado/triage" (antigüedad, responsable, prioridad). Antes el ícono
                    de audio vivía en el cluster derecho, compitiendo por espacio con
                    antigüedad+responsable+prioridad — 4 chips angostos amontonados. */}
                <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
                        <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full text-white truncate"
                            style={{ backgroundColor: tarea.cliente_color }}
                            title={tarea.empresa}
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-white/50"></span>
                            {tarea.empresa}
                        </span>
                        <span className="text-xs text-slate-500">
                            {/* TIPO_TAREA_LABELS ya arranca con el emoji (ej. "🚀 Nuevo Módulo"),
                                así que el índice [0] de split(' ') era el emoji, no la palabra —
                                se mostraba "🚀 🚀" duplicado y sin texto. Es [1]. */}
                            {tipoIcons[tarea.tipo_tarea]} {TIPO_TAREA_LABELS[tarea.tipo_tarea].split(' ')[1]}
                        </span>
                        {tarea.audio_origen_id && (
                            <span
                                className="text-[10px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-600"
                                title="Se creó desde un audio — abrí la tarjeta para ver el detalle"
                            >
                                🎙️
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Antes se mostraba siempre, incluso "⏱ 3d" recién llegada a la
                            columna — ruido en la mayoría de las tarjetas sanas. Ahora solo
                            aparece cuando de verdad hay algo para revisar (más de 3 días),
                            así resalta por excepción en vez de competir con todo lo demás. */}
                        {edadNivel !== 'ok' && (
                            <span
                                className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${edadColor} ${edadRing}`}
                                title={`Hace ${diasEnColumna} día(s) en esta columna${edadNivel === 'critico' ? ' — ⚠️ REVISAR URGENTE' : ''}`}
                            >
                                {edadNivel === 'critico' || edadNivel === 'alto' ? '⚠ ' : '⏱ '}{diasEnColumna}d
                            </span>
                        )}
                        {tarea.responsable && (
                            <div
                                className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[9px] font-bold"
                                title={`Responsable: ${tarea.responsable}`}
                            >
                                {getInitials(tarea.responsable)}
                            </div>
                        )}
                        <div className={`
                            w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold
                            ${PRIORIDAD_COLORS[tarea.prioridad as Prioridad].bg}
                            ${PRIORIDAD_COLORS[tarea.prioridad as Prioridad].text}
                        `}
                        title={`Prioridad: ${tarea.prioridad}`}
                        >
                            {PRIORIDAD_COLORS[tarea.prioridad as Prioridad].icon}
                        </div>
                    </div>
                </div>

                <h4 className={`
                    font-semibold text-sm leading-snug mb-2 line-clamp-2
                    ${isCompleted ? 'line-through text-slate-400' : 'text-slate-800'}
                `}>
                    {tarea.titulo}
                </h4>

                {tarea.descripcion && (
                    <p className="text-xs text-slate-500 line-clamp-1 mb-2">
                        {tarea.descripcion}
                    </p>
                )}

                <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <span
                        className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded ${
                            tarea.tamanio === 'XL'
                                ? 'bg-red-100 text-red-700 ring-1 ring-red-300'
                                : 'bg-slate-100 text-slate-500'
                        }`}
                        title={tarea.tamanio === 'XL'
                            ? 'Tamaño XL: considera dividir esta tarea en subtareas más pequeñas'
                            : `Tamaño: ${TAMANIO_LABELS[tarea.tamanio]}`
                        }
                    >
                        {TAMANIO_LABELS[tarea.tamanio]}
                    </span>

                    {tarea.clase_servicio !== 'estandar' && (
                        <span
                            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                esExpedito ? 'bg-red-500 text-white' : 'bg-indigo-100 text-indigo-700'
                            }`}
                            title={`Clase de servicio: ${CLASE_SERVICIO_LABELS[tarea.clase_servicio]}`}
                        >
                            {CLASE_SERVICIO_ICONS[tarea.clase_servicio]} {CLASE_SERVICIO_LABELS[tarea.clase_servicio]}
                        </span>
                    )}

                    {tarea.bloqueado && (
                        <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700 ring-1 ring-red-300"
                            title={tarea.motivo_bloqueo || 'Tarea bloqueada'}
                        >
                            🚩 Bloqueado
                        </span>
                    )}
                </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100/50">
                        <span className="text-[11px] text-slate-500 truncate max-w-[55%]" title={tarea.nombre_proyecto}>
                            📁 {tarea.nombre_proyecto}
                        </span>
                        <div className="flex items-center gap-1.5">
                            {tarea.fecha_limite && (
                                <span className={`
                                    text-[11px] flex items-center gap-1 font-medium
                                    ${overdue ? 'text-red-500' : 'text-slate-500'}
                                `}>
                                    {overdue ? '⚠️' : '📅'} {formatDate(tarea.fecha_limite)}
                                </span>
                            )}
                        </div>
                    </div>

                {isCompleted && (
                    <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                )}

                <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-blue-400/30 transition-colors pointer-events-none"></div>
            </motion.div>
        </div>
    );
};
